import { injectable, inject } from 'inversify';
import { DslRuntimeModule, moduleImplementation, RuntimeValue, runtimeValue, param, BooleanType,
         secondsAsDuration, Duration, DurationType, ExamType, QuizType, NumberType } from '@xlearn/common';
import { QuizReference } from '@/app/quiz';
import { ExamResultsQuerySymbol, ExamResultsQuery } from '../state';

@injectable()
export class ExamDslRuntimeModule implements DslRuntimeModule {

  public readonly ns = 'exam';

  constructor(
    @inject(ExamResultsQuerySymbol)
    private examResultsQuery: ExamResultsQuery) {

  }

  get implementations() {
    return [
      moduleImplementation('exam', (params) => this.exam(params), param(QuizType)),
      moduleImplementation('time', (params) => this.examTime(params), param(ExamType)),
      moduleImplementation('completed', (params) => this.isExamCompleted(params), param(ExamType)),
      moduleImplementation('notAttempted', (params) => this.isExamNotAttempted(params), param(ExamType)),
      moduleImplementation('incomplete', (params) => this.isExamIncomplete(params), param(ExamType)),
      moduleImplementation('passed', (params) => this.isExamPassed(params), param(ExamType)),
      moduleImplementation('failed', (params) => this.isExamFailed(params), param(ExamType)),
      moduleImplementation('attemptCount', (params) => this.attemptCount(params), param(ExamType)),
      moduleImplementation('percent', (params) => this.percent(params), param(ExamType)),
      moduleImplementation('score', (params) => this.score(params), param(ExamType))
    ];
  }

  private exam([ param ]: RuntimeValue[]): RuntimeValue {
    return param
  }

  private examTime(params: RuntimeValue[]) {
    const result = this.resolveExamResult(params)

    const seconds = result !== false ? result.durationSeconds : 0
    const duration = secondsAsDuration(seconds);
    return runtimeValue<Duration>(duration, DurationType);
  }

  private isExamCompleted(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const completed = result !== false ? true : false

    return runtimeValue(completed, BooleanType);
  }

  private isExamNotAttempted(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    // TODO: Session
    const notAttempted = result === false ? true : false

    return runtimeValue(notAttempted, BooleanType);
  }

  private isExamIncomplete(params: RuntimeValue[]): RuntimeValue {
    // TODO: use exam session to determine
    //throw new Error('notimpl')
    return runtimeValue(false, BooleanType)
  }

  private isExamPassed(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const passed = result !== false ? result.result === 'passed' : false

    return runtimeValue(passed, BooleanType);
  }

  private isExamFailed(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const passed = result !== false ? result.result === 'failed' : false

    return runtimeValue(passed, BooleanType);
  }

  private attemptCount(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const count = result !== false ? result.attemptCount : 0

    return runtimeValue(count, NumberType);
  }

  private percent(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const percent = result !== false ? result.score.percent : 0

    return runtimeValue(percent, NumberType);
  }

  private score(params: RuntimeValue[]): RuntimeValue {
    const result = this.resolveExamResult(params)
    const score = result !== false ? result.score.raw : 0

    return runtimeValue(score, NumberType);
  }

  private resolveExamResult([ param ]: RuntimeValue[]) {
    const reference = param.value as ExamReference
    const found = this.examResultsQuery.findExam(reference.id)
    if (found === false) {
      return false
    }

    return found
  }
}

type ExamReference = QuizReference

